import { useEffect, useRef, useState } from "react";
import type { CreativeContextSelection } from "@digital-twins/shared-types";
import { useTranslation } from "react-i18next";
import { ChevronDown, Palette } from "@digital-twins/design-system/icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { useCreativeLibrary } from "@/hooks/useCreativeLibrary";
import { PrivateImage } from "@/pages/KnowledgeHubPage/creatives/PrivateImage";
import { SubjectEditor } from "@/pages/KnowledgeHubPage/creatives/SubjectEditor";
import type { ImageCardCreativeFieldsProps } from "@/types/creative-library";
import { CreativeMainReference } from "./CreativeMainReference";
import { CreativeReferencePicker } from "./CreativeReferencePicker";
import {
  withBoard,
  withBrand,
  withProduct,
  withSubject,
} from "./imageCardSelection";

const NONE = "none";
const NEW_MODEL = "new-model";

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Text size="xs" color="muted">
        {props.label}
      </Text>
      {props.children}
    </div>
  );
}

/** Brand, Model, Product, Board and pinned images for one image approval card. */
export function ImageCardCreativeFields(props: ImageCardCreativeFieldsProps) {
  const {
    selection,
    disabled,
    onChange: onChoice,
    onLibraryChanged,
    refreshKey,
    isForcedOpen,
  } = props;

  const { t } = useTranslation("app");
  const library = useCreativeLibrary();
  const [creatingModel, setCreatingModel] = useState(false);
  const [extrasOpen, setExtrasOpen] = useState(false);
  const [isCleared, setIsCleared] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const lastRefreshKey = useRef(refreshKey);
  const hasOpenedForMissing = useRef(false);
  const { reload } = library;

  useEffect(() => {
    // A recheck reloads the library, so an item fixed there shows its name again.
    if (lastRefreshKey.current === refreshKey) {
      return;
    }
    lastRefreshKey.current = refreshKey;
    void reload();
  }, [refreshKey, reload]);

  const onChange = (next: CreativeContextSelection) => {
    // A new brand that drops an earlier choice says so; the next change clears the note.
    const isNewBrand = next.brandProfileToken !== selection.brandProfileToken;
    const isDropped =
      (["subjectToken", "productToken", "boardToken"] as const).some(
        (slot) => selection[slot] && !next[slot],
      ) || (next.referenceTokens?.length ?? 0) < (selection.referenceTokens?.length ?? 0);
    setIsCleared(isNewBrand && isDropped);
    onChoice(next);
  };

  const brand = selection.brandProfileToken ?? NONE;
  const subjectToken = selection.subjectToken ?? NONE;
  const productToken = selection.productToken ?? NONE;
  const boardToken = selection.boardToken ?? NONE;
  const selectedBrand = library.brands.find((item) => item.token === brand);
  const subjects = library.subjects.filter((item) => item.brandToken === brand);
  const subject = library.subjects.find((item) => item.token === subjectToken);
  const products = library.products.filter((item) => item.brandToken === brand);
  const boards = library.boards.filter(
    (board) =>
      brand === NONE ||
      board.brandToken === brand ||
      board.applicableBrandTokens.includes(brand),
  );
  const blocked = Boolean(disabled) || library.loading;
  const pinned = selection.referenceTokens ?? [];
  const triggerClass = "h-9 min-w-0 bg-transparent px-3 [&>span]:truncate";

  const hasMissingPin =
    !library.loading &&
    !library.refreshing &&
    pinned.some((token) => !library.creatives.some((item) => item.token === token));

  useEffect(() => {
    // A pinned image that is gone can only be taken off from the open list.
    if (hasMissingPin && !hasOpenedForMissing.current) {
      hasOpenedForMissing.current = true;
      setExtrasOpen(true);
    }
  }, [hasMissingPin]);

  const isLoaded = !library.loading && !library.refreshing;
  const hasMissingChoice =
    isLoaded &&
    ((brand !== NONE && !selectedBrand) ||
      (subjectToken !== NONE && !subject) ||
      (productToken !== NONE && !library.products.some((item) => item.token === productToken)) ||
      (boardToken !== NONE && !library.boards.some((item) => item.token === boardToken)));
  const shouldOpen = Boolean(isForcedOpen) || hasMissingChoice || hasMissingPin;

  useEffect(() => {
    // Once the card needed attention the fields stay open, even after the block clears.
    if (shouldOpen) {
      setIsOpen(true);
    }
  }, [shouldOpen]);

  const isExpanded = isOpen || shouldOpen;
  const summaryNames = [
    selectedBrand?.name,
    subject?.name,
    library.products.find((item) => item.token === productToken)?.name,
    library.boards.find((item) => item.token === boardToken)?.name,
  ].filter(Boolean);
  const brandLogo = selectedBrand
    ? library.creatives.find(
        (item) =>
          item.role === "logo" &&
          (item.brandToken === selectedBrand.token ||
            selectedBrand.materialTokens?.includes(item.token)),
      )
    : undefined;
  const hasNothingSet =
    brand === NONE && subjectToken === NONE && productToken === NONE && boardToken === NONE;

  const nothingToChoose =
    !library.loading &&
    library.brands.length === 0 &&
    library.boards.length === 0 &&
    library.creatives.length === 0 &&
    !selection.brandProfileToken;
  if (nothingToChoose) {
    return null;
  }

  return (
    <div
      className="flex min-w-0 flex-col gap-3 rounded-xl border border-border/60 p-3"
      data-testid="image-card-creative-fields"
    >
      {!isExpanded && (
        <div
          className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1"
          data-testid="image-card-fields-summary"
        >
          {hasNothingSet && !library.loading ? (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground"
              data-testid="image-card-fields-open"
              onClick={() => setIsOpen(true)}
            >
              {t("threadImage.approval.fieldsAdd")}
            </button>
          ) : (
            <>
              {selectedBrand &&
                (brandLogo ? (
                  <PrivateImage
                    source={brandLogo.mediaUrl}
                    alt=""
                    className="h-5 w-5 shrink-0 rounded object-contain"
                    testId="image-card-fields-brand-logo"
                  />
                ) : (
                  <Palette
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden
                    data-testid="image-card-fields-brand-icon"
                  />
                ))}
              {summaryNames.length > 0 && (
                <Text size="xs" className="min-w-0 break-words">
                  {summaryNames.join(" · ")}
                </Text>
              )}
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                data-testid="image-card-fields-open"
                onClick={() => setIsOpen(true)}
              >
                {t("threadImage.approval.fieldsChange")}
                <ChevronDown className="h-3 w-3" aria-hidden />
              </button>
            </>
          )}
        </div>
      )}
      {isExpanded && (
      <>
      <div className="grid min-w-0 grid-cols-2 gap-2">
        <Field label={t("creativeLibrary.brand")}>
          <Select
            value={brand}
            disabled={blocked}
            onValueChange={(value) =>
              onChange(withBrand(selection, value === NONE ? null : value, library))
            }
          >
            <SelectTrigger
              className={triggerClass}
              aria-label={t("creativeLibrary.brand")}
              data-testid="thread-creative-brand"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t("creativeLibrary.noBrand")}</SelectItem>
              {library.brands.map((item) => (
                <SelectItem key={item.token} value={item.token}>
                  {item.parentBrandToken
                    ? `${library.brands.find((parent) => parent.token === item.parentBrandToken)?.name ?? ""} / `
                    : ""}
                  {item.name}
                </SelectItem>
              ))}
              {brand !== NONE && !selectedBrand && !library.loading && !library.refreshing && (
                <SelectItem value={brand} disabled>
                  {t("creativeLibrary.unavailableSelection")}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("creativeLibrary.subject")}>
          <Select
            value={subjectToken}
            disabled={blocked || brand === NONE}
            onValueChange={(value) => {
              if (value === NEW_MODEL) {
                setCreatingModel(true);
                return;
              }
              onChange(withSubject(selection, value === NONE ? null : value, library));
            }}
          >
            <SelectTrigger
              className={triggerClass}
              aria-label={t("creativeLibrary.subject")}
              data-testid="thread-creative-subject"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t("creativeLibrary.noSubject")}</SelectItem>
              {subjects.map((item) => (
                <SelectItem key={item.token} value={item.token}>
                  {item.name}
                </SelectItem>
              ))}
              {subjectToken !== NONE && !subject && !library.loading && !library.refreshing && (
                <SelectItem value={subjectToken} disabled>
                  {t("creativeLibrary.unavailableSelection")}
                </SelectItem>
              )}
              {selectedBrand?.canEdit && (
                <SelectItem value={NEW_MODEL} data-testid="thread-creative-subject-new">
                  {t("creativeLibrary.newSubject")}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("creativeLibrary.product")}>
          <Select
            value={productToken}
            disabled={blocked || brand === NONE}
            onValueChange={(value) =>
              onChange(
                withProduct(
                  selection,
                  value === NONE ? null : value,
                  library.creatives,
                ),
              )
            }
          >
            <SelectTrigger
              className={triggerClass}
              aria-label={t("creativeLibrary.product")}
              data-testid="thread-creative-product"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t("creativeLibrary.noProduct")}</SelectItem>
              {products.map((item) => (
                <SelectItem key={item.token} value={item.token}>
                  {item.name}
                  {item.sku ? ` · ${item.sku}` : ""}
                </SelectItem>
              ))}
              {productToken !== NONE &&
                !products.some((item) => item.token === productToken) &&
                !library.loading &&
                !library.refreshing && (
                  <SelectItem value={productToken} disabled>
                    {t("creativeLibrary.unavailableSelection")}
                  </SelectItem>
                )}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("creativeLibrary.board")}>
          <Select
            value={boardToken}
            disabled={blocked}
            onValueChange={(value) =>
              onChange(
                withBoard(
                  selection,
                  value === NONE ? null : value,
                  library.boards,
                ),
              )
            }
          >
            <SelectTrigger
              className={triggerClass}
              aria-label={t("creativeLibrary.board")}
              data-testid="thread-creative-board"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t("creativeLibrary.noBoard")}</SelectItem>
              {boards.map((item) => (
                <SelectItem key={item.token} value={item.token}>
                  {item.name}
                </SelectItem>
              ))}
              {boardToken !== NONE &&
                !boards.some((item) => item.token === boardToken) &&
                !library.loading &&
                !library.refreshing && (
                  <SelectItem value={boardToken} disabled>
                    {t("creativeLibrary.unavailableSelection")}
                  </SelectItem>
                )}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          aria-expanded={extrasOpen}
          data-testid="image-card-creative-extras"
          onClick={() => setExtrasOpen((open) => !open)}
        >
          {t("creativeLibrary.imagesAndUsage")}
          {pinned.length > 0 && ` (${pinned.length})`}
          <ChevronDown
            className={`h-3 w-3 transition-transform ${extrasOpen ? "rotate-180" : ""}`}
            aria-hidden
          />
        </button>
        {!shouldOpen && (
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            data-testid="image-card-fields-hide"
            onClick={() => setIsOpen(false)}
          >
            {t("threadImage.approval.fieldsHide")}
            <ChevronDown className="h-3 w-3 rotate-180" aria-hidden />
          </button>
        )}
      </div>
      {extrasOpen && (
        <div className="flex min-w-0 flex-col gap-3">
          {subject && (
            <CreativeMainReference
              subject={subject}
              items={library.creatives}
              pinnedTokens={
                library.boards.find((item) => item.token === boardToken)?.definition
                  ?.referenceTokens ?? []
              }
              selection={selection}
              disabled={blocked}
              onChange={(referenceTokens) =>
                onChange({ ...selection, referenceTokens })
              }
            />
          )}
          <Field label={t("creativeLibrary.intendedUsage")}>
            <Select
              value={selection.intendedUse ?? "internal"}
              disabled={blocked}
              onValueChange={(value) =>
                onChange({
                  ...selection,
                  intendedUse: value as "internal" | "external",
                })
              }
            >
              <SelectTrigger
                className={triggerClass}
                aria-label={t("creativeLibrary.intendedUsage")}
                data-testid="thread-creative-intended-use"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="internal">
                  {t("creativeLibrary.contextInternal")}
                </SelectItem>
                <SelectItem value="external">
                  {t("creativeLibrary.contextExternal")}
                </SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <CreativeReferencePicker
            items={library.creatives.filter(
              (item) =>
                (brand === NONE ||
                  !item.brandToken ||
                  item.brandToken === brand ||
                  pinned.includes(item.token)) &&
                (item.role !== "product" ||
                  productToken === NONE ||
                  item.productToken === productToken ||
                  pinned.includes(item.token)) &&
                (item.role !== "identity" ||
                  !subject ||
                  subject.referenceTokens.includes(item.token)),
            ).sort(
              (a, b) =>
                Number(brand !== NONE && b.brandToken === brand) -
                Number(brand !== NONE && a.brandToken === brand),
            )}
            primaryReferenceToken={subject?.primaryReferenceToken}
            selected={pinned}
            disabled={blocked}
            isAlwaysOpen
            intendedUse={selection.intendedUse ?? "internal"}
            onChange={(referenceTokens) =>
              onChange({ ...selection, referenceTokens })
            }
          />
        </div>
      )}
      </>
      )}
      {isCleared && (
        <Text size="xs" color="muted" role="status" data-testid="image-card-selection-cleared">
          {t("creativeLibrary.selectionCleared")}
        </Text>
      )}
      {library.error && (
        <div className="flex items-center justify-between gap-2">
          <Text size="xs" className="text-destructive" role="alert">
            {t("creativeLibrary.contextFailed")}
          </Text>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void library.reload()}
            data-testid="thread-creative-retry"
          >
            {t("creativeLibrary.retry")}
          </Button>
        </div>
      )}
      {creatingModel && selectedBrand && (
        <SubjectEditor
          subject={null}
          brand={selectedBrand}
          creatives={[]}
          createOnly
          onClose={() => setCreatingModel(false)}
          onSaved={() => {
            void library.reload();
            onLibraryChanged?.();
          }}
          onUse={() => {}}
          onPersisted={(created) => {
            onChange(withSubject(selection, created.token, library));
          }}
        />
      )}
    </div>
  );
}
